import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MessageSquare, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AskLawyer = ({ user, onLogout }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [answerDialog, setAnswerDialog] = useState({ open: false, question: null });
  const [formData, setFormData] = useState({
    title: "",
    question: "",
    category: "civil"
  });
  const [answerText, setAnswerText] = useState("");

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      setQuestions(response.data);
    } catch (error) {
      toast.error("خطأ في تحميل الأسئلة");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/questions`, formData);
      toast.success("تم إرسال سؤالك بنجاح");
      setDialogOpen(false);
      setFormData({ title: "", question: "", category: "civil" });
      fetchQuestions();
    } catch (error) {
      toast.error("خطأ في إرسال السؤال");
    }
  };

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/questions/${answerDialog.question.id}/answer`, {
        answer: answerText
      });
      toast.success("تم إرسال الإجابة بنجاح");
      setAnswerDialog({ open: false, question: null });
      setAnswerText("");
      fetchQuestions();
    } catch (error) {
      toast.error("خطأ في إرسال الإجابة");
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      civil: "مدني",
      criminal: "جنائي",
      commercial: "تجاري",
      labor: "عمل",
      personal_status: "أحوال شخصية"
    };
    return labels[category] || category;
  };

  const getStatusBadge = (status) => {
    if (status === "open") return <span className="badge badge-yellow">قيد الانتظار</span>;
    if (status === "answered") return <span className="badge badge-green">تمت الإجابة</span>;
    return <span className="badge badge-blue">{status}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="ask-lawyer-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              {["lawyer", "jurist", "law_student"].includes(user?.user_type) ? "الأسئلة المستلمة" : "اسأل محامي"}
            </h1>
            <p className="text-lg text-slate-600">
              {["lawyer", "jurist", "law_student"].includes(user?.user_type)
                ? "إجب على أسئلة المواطنين"
                : "اطرح أسئلتك القانونية واحصل على إجابات من محامين"}
            </p>
          </div>

          {user?.user_type === "citizen" && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="ask-question-button">
                  <MessageSquare className="w-5 h-5" />
                  اطرح سؤالاً
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="ask-question-dialog">
                <DialogHeader>
                  <DialogTitle>سؤال جديد</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitQuestion} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">عنوان السؤال *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      data-testid="question-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">التصنيف *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger id="category" data-testid="question-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="civil">مدني</SelectItem>
                        <SelectItem value="criminal">جنائي</SelectItem>
                        <SelectItem value="commercial">تجاري</SelectItem>
                        <SelectItem value="labor">عمل</SelectItem>
                        <SelectItem value="personal_status">أحوال شخصية</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="question">السؤال *</Label>
                    <Textarea
                      id="question"
                      rows={6}
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      required
                      data-testid="question-text-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      إلغاء
                    </Button>
                    <Button type="submit" data-testid="submit-question-button">إرسال السؤال</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Questions List */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-20 bg-slate-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : questions.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">
                {["lawyer", "jurist", "law_student"].includes(user?.user_type)
                  ? "لا توجد أسئلة حالياً"
                  : "لم تطرح أي سؤال بعد. ابدأ بطرح سؤالك الأول!"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {questions.map((q) => (
              <Card key={q.id} className="hover:shadow-lg transition-shadow" data-testid={`question-card-${q.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{q.title}</CardTitle>
                      <div className="flex items-center gap-3 text-sm">
                        {getStatusBadge(q.status)}
                        <span className="text-slate-500">{getCategoryLabel(q.category)}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">السؤال:</p>
                      <p className="text-slate-700">{q.question}</p>
                    </div>

                    {q.answer && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="font-semibold text-green-900 mb-1 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          الإجابة:
                        </p>
                        <p className="text-green-800">{q.answer}</p>
                      </div>
                    )}

                    {["lawyer", "jurist", "law_student"].includes(user?.user_type) && q.status === "open" && (
                      <div className="flex justify-end">
                        <Button
                          onClick={() => setAnswerDialog({ open: true, question: q })}
                          className="gap-2"
                          data-testid={`answer-question-${q.id}`}
                        >
                          <Send className="w-4 h-4" />
                          إجابة على السؤال
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Answer Dialog */}
        <Dialog open={answerDialog.open} onOpenChange={(open) => setAnswerDialog({ open, question: null })}>
          <DialogContent data-testid="answer-dialog">
            <DialogHeader>
              <DialogTitle>إجابة علإ السؤال</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitAnswer} className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-semibold text-slate-900 mb-1">السؤال:</p>
                <p className="text-slate-700">{answerDialog.question?.question}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="answer">إجابتك *</Label>
                <Textarea
                  id="answer"
                  rows={6}
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  required
                  data-testid="answer-text-input"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAnswerDialog({ open: false, question: null })}
                >
                  إلغاء
                </Button>
                <Button type="submit" data-testid="submit-answer-button">إرسال الإجابة</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AskLawyer;
